import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Play, RotateCcw, Home, ChevronRight, Sparkles, Volume2, VolumeX, Pause, Shield } from 'lucide-react';
import {
  LEVELS, MAX_LEVEL, biomeForLevel, ANCHORS, computeStars,
} from '@/lib/calmCommandLevels';
import {
  recordRun, playSfx, isSfxMuted, setSfxMuted,
  getCC, saveCCLevelResult, saveCCEndless,
  subscribe, getMeta,
} from '@/lib/loungeMeta';
import { useLoungeCountdown, LoungeCountdown } from '@/lib/loungeGame';
import CalmCommandLevelSelect from './lounge/CalmCommandLevelSelect';
import StarBadge from './lounge/StarBadge';

// Shared canvas/gameplay primitives extracted for testability + clarity.
// See /app/frontend/src/lib/calmCommand/*.js — refactor template as of iter 57.
import {
  TAU, ANCHOR_Y_FRAC, ANCHOR_RADIUS, HORIZON_Y_FRAC,
  clamp, rand, formatMMSS,
} from '@/lib/calmCommand/constants';
import { buildScene, drawScene } from '@/lib/calmCommand/scene';
import { buildWeather, drawWeather } from '@/lib/calmCommand/weather';

/* ============================================================================
 * Calm Command — Missile Command-style neon wellness Lounge game.
 * ============================================================================ */
export default function CalmCommand() {
  const canvasRef = useRef(null);
  const wrapperRef = useRef(null);
  const sectionRef = useRef(null);
  const rafRef = useRef(null);
  const lastTsRef = useRef(0);
  const stateRef = useRef(null);
  const pausedRef = useRef(false);

  const [view, setView] = useState('map'); // map | ready | playing | over
  const [cc, setCc] = useState(() => getCC());
  const [levelId, setLevelId] = useState(() => Math.min(getCC().lastLevel || 1, 30));
  const [mode, setMode] = useState('campaign'); // campaign | endless
  const [muted, setMuted] = useState(() => isSfxMuted());
  useEffect(() => subscribe(() => { setCc(getMeta().cc); setMuted(isSfxMuted()); }), []);

  const [hudScore, setHudScore] = useState(0);
  const [hudAnchors, setHudAnchors] = useState(6);
  const [hudTime, setHudTime] = useState(0);           // seconds remaining
  const [paused, setPaused] = useState(false);
  const [runResult, setRunResult] = useState(null);

  const { countdown, beginCountdown, cancelCountdown } = useLoungeCountdown();
  useEffect(() => { if (view !== 'playing') cancelCountdown(); }, [view]); // eslint-disable-line react-hooks/exhaustive-deps

  const level = useMemo(() => LEVELS[levelId - 1], [levelId]);
  const biome = useMemo(() => biomeForLevel(levelId), [levelId]);
  const bestStars = cc?.levelStars?.[levelId] ?? 0;

  const scrollSectionIntoView = () => {
    requestAnimationFrame(() => requestAnimationFrame(() => {
      sectionRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }));
  };

  /* ---- Canvas resize ---- */
  const fit = useCallback(() => {
    const canvas = canvasRef.current;
    const wrap = wrapperRef.current;
    if (!canvas || !wrap) return;
    const w = Math.min(760, wrap.clientWidth);
    let h = Math.max(360, Math.min(480, Math.round(w * 0.68)));
    // Full-screen embed (native WebView): grow to fill the viewport height.
    if (canvas.closest('[data-embed]')) {
      const top = wrap.getBoundingClientRect().top;
      const avail = Math.round((window.visualViewport?.height || window.innerHeight) - top - 12);
      if (avail > h) h = Math.min(avail, 1000);
    }
    canvas.width = w;
    canvas.height = h;
    canvas.style.width = w + 'px';
    canvas.style.height = h + 'px';
    return { w, h };
  }, []);

  /* ---- Build fresh play state ---- */
  const buildState = useCallback((W, H, cfg, mo, biomeCfg) => {
    // Anchor positions along the bottom
    const anchorY = H * ANCHOR_Y_FRAC;
    const marginX = W * 0.09;
    const step = (W - marginX * 2) / (ANCHORS.length - 1);
    const anchors = ANCHORS.map((a, i) => ({
      ...a,
      x: marginX + step * i,
      y: anchorY,
      alive: true,
      pulse: rand(0, TAU),
    }));

    // Cache stars for parallax
    const stars = Array.from({ length: 50 }, () => ({
      x: rand(0, W), y: rand(0, H * HORIZON_Y_FRAC),
      s: rand(0.6, 2.0), tw: rand(0, TAU),
    }));

    // Themed ground scene — deterministic per biome for replay consistency.
    const scene = buildScene(biomeCfg.scene, biomeCfg, W, H);
    const weather = buildWeather(biomeCfg.scene, W, H);

    return {
      W, H, cfg, mode: mo,
      anchors,
      anchorY,
      missiles: [],       // { x, y, vx, vy, color, trail:[{x,y}], splitter, split }
      blasts: [],         // { x, y, r, maxR, t, color, alive }
      launchers: [],      // { x, y, tx, ty, vx, vy, color, targetR }
      particles: [],
      launchT: 0,         // cooldown timer
      spawnT: cfg.spawnEvery,
      spawned: 0,
      // Timed survival: campaign uses cfg.duration; endless runs forever.
      timeLeft: mo === 'campaign' ? (cfg.duration || 60) : Infinity,
      totalTime: mo === 'campaign' ? (cfg.duration || 60) : 0,
      neutralized: 0,     // score
      lost: 0,
      stars,
      scene,
      weather,
      parallaxT: 0,
      gameOver: false,
      cleared: false,
    };
  }, []);

  /* ---- Spawn helpers ---- */
  function spawnMissile(s) {
    const cfg = s.cfg;
    const W = s.W;
    // Pick a random spawn point along the top edge
    const x = rand(W * 0.08, W * 0.92);
    // Pick a target anchor (or a random point on the ground if all destroyed)
    const alive = s.anchors.filter(a => a.alive);
    let tx, ty;
    if (alive.length) {
      const t = alive[Math.floor(Math.random() * alive.length)];
      tx = t.x + rand(-12, 12); ty = t.y;
    } else {
      tx = rand(W * 0.1, W * 0.9); ty = s.H * ANCHOR_Y_FRAC;
    }
    const dx = tx - x, dy = ty - 0;
    const dist = Math.max(1, Math.hypot(dx, dy));
    const speedFactor = cfg.missileSpeed / 60;
    const isSplitter = Math.random() < (cfg.splitterFrac ?? 0);
    const missile = {
      x, y: 0,
      vx: (dx / dist) * speedFactor,
      vy: (dy / dist) * speedFactor,
      targetX: tx, targetY: ty,
      color: isSplitter ? '#ff3a7d' : '#ff7db0',
      splitter: isSplitter,
      split: false,
      trail: [],
    };
    s.missiles.push(missile);
    s.spawned += 1;
  }

  function spawnBlast(s, x, y, color) {
    s.blasts.push({
      x, y,
      r: 0,
      maxR: s.cfg.blastRadius,
      t: 0,
      dur: 1.2,           // seconds
      color,
    });
    playSfx('boom', { volume: 0.22 });
    // Sparkles
    for (let i = 0; i < 12; i++) {
      const a = rand(0, TAU);
      const v = rand(0.8, 2.0);
      s.particles.push({
        x, y,
        vx: Math.cos(a) * v, vy: Math.sin(a) * v,
        life: rand(0.4, 0.9), maxLife: 0.9,
        color, size: rand(1.5, 3),
      });
    }
  }

  function fireInterceptor(s, tx, ty) {
    if (s.launchT > 0) return false;
    const alive = s.anchors.filter(a => a.alive);
    if (!alive.length) return false;
    // Pick the anchor nearest to the horizontal target
    let best = alive[0], bestDx = Math.abs(alive[0].x - tx);
    for (const a of alive) {
      const dx = Math.abs(a.x - tx);
      if (dx < bestDx) { best = a; bestDx = dx; }
    }
    const dx = tx - best.x, dy = ty - best.y;
    const dist = Math.max(1, Math.hypot(dx, dy));
    const speed = 8.2; // px/frame
    s.launchers.push({
      x: best.x, y: best.y,
      tx, ty,
      vx: (dx / dist) * speed,
      vy: (dy / dist) * speed,
      color: best.color,
      dist,
      travelled: 0,
      trail: [],
    });
    s.launchT = s.cfg.launchCooldown;
    playSfx('laser', { volume: 0.18 });
    return true;
  }

  /* ---- Main loop ---- */
  const loop = useCallback((ts) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const s = stateRef.current;
    if (!s) return;

    if (!lastTsRef.current) lastTsRef.current = ts;
    const dt = Math.min(0.033, (ts - lastTsRef.current) / 1000);
    lastTsRef.current = ts;

    if (!pausedRef.current && !s.gameOver && !s.cleared) {
      update(s, dt);
    }
    render(ctx, s);
    rafRef.current = requestAnimationFrame(loop);
    // Stable RAF loop — reads mutable state via refs. Deps intentionally empty.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function update(s, dt) {
    s.parallaxT += dt;
    s.launchT = Math.max(0, s.launchT - dt);

    // Survival timer (campaign only)
    if (s.mode === 'campaign') {
      s.timeLeft = Math.max(0, s.timeLeft - dt);
    }

    // Missile spawn cadence — continuous for the whole run
    s.spawnT -= dt;
    if (s.spawnT <= 0) {
      spawnMissile(s);
      // Endless gets progressively faster with score; campaign keeps its base cadence.
      const jitter = rand(0.7, 1.15);
      const scoreRamp = s.mode === 'campaign' ? 0 : Math.min(0.5, s.neutralized * 0.004);
      s.spawnT = Math.max(0.32, s.cfg.spawnEvery * jitter - scoreRamp);
    }

    // Missiles → trail + advance
    for (let i = s.missiles.length - 1; i >= 0; i--) {
      const m = s.missiles[i];
      m.trail.push({ x: m.x, y: m.y });
      if (m.trail.length > 18) m.trail.shift();
      m.x += m.vx * dt * 60;
      m.y += m.vy * dt * 60;

      // Splitter behavior — split half-way down
      if (m.splitter && !m.split && m.y > s.H * 0.42) {
        m.split = true;
        // Spawn two forked missiles left and right
        const speed = Math.hypot(m.vx, m.vy);
        const baseA = Math.atan2(m.vy, m.vx);
        for (const sign of [-1, 1]) {
          const a = baseA + sign * 0.35;
          s.missiles.push({
            x: m.x, y: m.y,
            vx: Math.cos(a) * speed, vy: Math.sin(a) * speed,
            targetX: m.targetX, targetY: m.targetY,
            color: '#ff5c8a',
            splitter: false, split: false,
            trail: [],
          });
        }
        // Remove the parent
        s.missiles.splice(i, 1);
        continue;
      }

      // Reached the anchor line?
      if (m.y >= s.anchorY) {
        // Find the nearest alive anchor within range
        let hit = null, hitDx = 24;
        for (const a of s.anchors) {
          if (!a.alive) continue;
          const dx = Math.abs(a.x - m.x);
          if (dx < hitDx) { hit = a; hitDx = dx; }
        }
        if (hit) {
          hit.alive = false;
          s.lost += 1;
          // Big explosion at the anchor
          spawnBlast(s, hit.x, hit.y, '#ff5c8a');
          s.shakeT = 0.35;
          playSfx('hit', { volume: 0.36 });
        }
        s.missiles.splice(i, 1);
      }
    }

    // Interceptors — travel then explode
    for (let i = s.launchers.length - 1; i >= 0; i--) {
      const lc = s.launchers[i];
      lc.trail.push({ x: lc.x, y: lc.y });
      if (lc.trail.length > 20) lc.trail.shift();
      lc.x += lc.vx * dt * 60;
      lc.y += lc.vy * dt * 60;
      lc.travelled += Math.hypot(lc.vx, lc.vy) * dt * 60;
      if (lc.travelled >= lc.dist) {
        spawnBlast(s, lc.tx, lc.ty, lc.color);
        s.launchers.splice(i, 1);
      }
    }

    // Blasts — grow, then contract, then die
    for (let i = s.blasts.length - 1; i >= 0; i--) {
      const bl = s.blasts[i];
      bl.t += dt;
      const p = bl.t / bl.dur;
      // 0→0.35: grow.  0.35→1: hold and fade.
      bl.r = p < 0.35 ? bl.maxR * (p / 0.35) : bl.maxR * (1 - (p - 0.35) * 0.25);
      if (bl.t >= bl.dur) {
        s.blasts.splice(i, 1);
      }
    }

    // Blast ↔ Missile collision (all active blasts vs all missiles)
    for (let i = s.missiles.length - 1; i >= 0; i--) {
      const m = s.missiles[i];
      let killed = false;
      for (const bl of s.blasts) {
        const dx = m.x - bl.x, dy = m.y - bl.y;
        if (dx * dx + dy * dy < bl.r * bl.r) {
          s.missiles.splice(i, 1);
          s.neutralized += 1;
          // Small pop
          for (let k = 0; k < 8; k++) {
            const a = rand(0, TAU);
            const v = rand(0.8, 1.6);
            s.particles.push({
              x: m.x, y: m.y,
              vx: Math.cos(a) * v, vy: Math.sin(a) * v,
              life: 0.5, maxLife: 0.7,
              color: bl.color, size: rand(1.2, 2.4),
            });
          }
          setHudScore(s.neutralized);
          playSfx('powerup', { volume: 0.16 });
          killed = true;
          break;
        }
      }
      if (killed) continue;
    }

    // Particles
    for (const p of s.particles) {
      p.x += p.vx;
      p.y += p.vy;
      p.vx *= 0.96;
      p.vy *= 0.96;
      p.life -= dt;
    }
    s.particles = s.particles.filter(p => p.life > 0);

    // HUD counts
    const aliveCount = s.anchors.filter(a => a.alive).length;
    setHudAnchors(aliveCount);
    if (s.mode === 'campaign') setHudTime(Math.ceil(s.timeLeft));

    // Lose: all anchors gone
    if (aliveCount === 0 && !s.gameOver) {
      s.gameOver = true;
      queueRunOver(s, false);
    }
    // Win (campaign): timer runs out with at least one anchor alive
    if (s.mode === 'campaign' && !s.cleared && !s.gameOver && s.timeLeft <= 0 && aliveCount > 0) {
      s.cleared = true;
      queueRunOver(s, true);
    }
  }

  const queueRunOver = (s, cleared) => {
    setTimeout(() => finalizeRun(s, cleared), cleared ? 600 : 700);
  };

  const finalizeRun = (s, cleared) => {
    if (view === 'over') return;
    const anchorsLeft = s.anchors.filter(a => a.alive).length;
    const stars = s.mode === 'campaign'
      ? computeStars(level, { cleared, anchorsLeft })
      : 0;
    const result = {
      level: levelId,
      score: s.neutralized,
      tokens: anchorsLeft,          // used for FP bonus
      cleared,
      perfect: cleared && anchorsLeft === 6,
      stars,
      neutralized: s.neutralized,
      anchorsLeft,
      mode: s.mode,
    };
    setRunResult(result);
    setView('over');
    if (s.mode === 'campaign') {
      saveCCLevelResult(levelId, { stars, cleared, anchorsLeft, neutralized: s.neutralized });
    } else {
      saveCCEndless(s.neutralized);
    }
    recordRun('command', result);
    playSfx(cleared ? 'levelClear' : 'collision', { volume: 0.35 });
  };

  /* ---- Rendering ---- */
  function render(ctx, s) {
    const { W, H } = s;
    // Sky gradient
    const g = ctx.createLinearGradient(0, 0, 0, H);
    g.addColorStop(0, biome.bg[0]);
    g.addColorStop(1, biome.bg[1]);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);

    // Nebula wash
    const haze = ctx.createRadialGradient(W * 0.75, H * 0.3, 20, W * 0.75, H * 0.3, W * 0.7);
    haze.addColorStop(0, `${biome.glow}30`);
    haze.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = haze;
    ctx.fillRect(0, 0, W, H);

    // Stars
    for (const st of s.stars) {
      const tw = 0.5 + 0.5 * Math.sin(s.parallaxT * 2 + st.tw);
      ctx.globalAlpha = 0.35 + tw * 0.5;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(st.x, st.y, st.s, st.s);
    }
    ctx.globalAlpha = 1;

    // Horizon grid
    const horY = H * HORIZON_Y_FRAC;
    const gradGround = ctx.createLinearGradient(0, horY, 0, H);
    gradGround.addColorStop(0, `${biome.accent}22`);
    gradGround.addColorStop(1, 'rgba(0,0,0,0.35)');
    ctx.fillStyle = gradGround;
    ctx.fillRect(0, horY, W, H - horY);

    // Themed ground scene (city / forest / desert / coast / mountains / prism)
    drawScene(ctx, s.scene, biome, W, H, horY, s.parallaxT);

    // Weather overlay (snow / sand / spray / leaves / motes / sparkles)
    drawWeather(ctx, s.weather, biome, W, H, horY, s.parallaxT);

    ctx.strokeStyle = `${biome.accent}66`;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, horY); ctx.lineTo(W, horY); ctx.stroke();
    // Perspective grid lines
    ctx.strokeStyle = `${biome.accent}22`;
    for (let x = 0; x <= W; x += 40) {
      ctx.beginPath();
      ctx.moveTo(x, horY);
      const vanish = W / 2;
      ctx.lineTo(vanish + (x - vanish) * 3.5, H);
      ctx.stroke();
    }
    for (let i = 1; i <= 4; i++) {
      const y = horY + ((H - horY) * i) / 5;
      ctx.beginPath();
      ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
    }

    // Missile trails + heads
    for (const m of s.missiles) {
      // Movement direction (angle) — used to orient the missile body
      const dir = Math.atan2(m.vy, m.vx);

      // Smoky exhaust trail — small warm particles behind the missile
      for (let i = 0; i < m.trail.length; i++) {
        const t = m.trail[i];
        const a = i / m.trail.length;                    // 0 = oldest, 1 = newest
        const r = 1.4 + a * 1.6;                          // trail widens toward the missile
        ctx.globalAlpha = a * 0.55;
        ctx.fillStyle = m.color;
        ctx.beginPath();
        ctx.arc(t.x, t.y, r, 0, TAU);
        ctx.fill();
      }
      ctx.globalAlpha = 1;

      // Faint aim line — dashed from missile to target
      ctx.save();
      ctx.setLineDash([3, 6]);
      ctx.strokeStyle = `${m.color}33`;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(m.x, m.y);
      ctx.lineTo(m.targetX, m.targetY);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.restore();

      // ── Missile body (oriented to direction of travel) ──
      ctx.save();
      ctx.translate(m.x, m.y);
      ctx.rotate(dir);

      // Exhaust flame at the tail — animated with parallaxT for flicker
      const flick = 0.75 + 0.25 * Math.sin(s.parallaxT * 40 + m.trail.length);
      ctx.save();
      ctx.shadowColor = '#ffcf3a';
      ctx.shadowBlur = 16;
      const grad = ctx.createLinearGradient(-16, 0, -6, 0);
      grad.addColorStop(0, 'rgba(255,207,58,0)');
      grad.addColorStop(0.6, '#ffcf3a');
      grad.addColorStop(1, '#ffffff');
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.moveTo(-16 * flick, -3);
      ctx.lineTo(-6, -2);
      ctx.lineTo(-6, 2);
      ctx.lineTo(-16 * flick, 3);
      ctx.closePath();
      ctx.fill();
      ctx.restore();

      const body = m.color;
      const rim  = m.splitter ? '#ff3a7d' : '#ff7db0';

      // Rear fins (two triangles top+bottom near the tail)
      ctx.save();
      ctx.shadowColor = rim;
      ctx.shadowBlur = 6;
      ctx.fillStyle = body;
      ctx.strokeStyle = rim;
      ctx.lineWidth = 1;
      // Top fin
      ctx.beginPath();
      ctx.moveTo(-6, -2);
      ctx.lineTo(-10, -6);
      ctx.lineTo(-4, -2);
      ctx.closePath();
      ctx.fill(); ctx.stroke();
      // Bottom fin
      ctx.beginPath();
      ctx.moveTo(-6, 2);
      ctx.lineTo(-10, 6);
      ctx.lineTo(-4, 2);
      ctx.closePath();
      ctx.fill(); ctx.stroke();
      ctx.restore();

      // Cylindrical body — capsule shape with neon rim
      ctx.save();
      ctx.shadowColor = rim;
      ctx.shadowBlur = 14;
      // Filled body
      const bodyGrad = ctx.createLinearGradient(0, -3, 0, 3);
      bodyGrad.addColorStop(0, `${body}`);
      bodyGrad.addColorStop(0.5, '#ffffff');
      bodyGrad.addColorStop(1, `${body}`);
      ctx.fillStyle = bodyGrad;
      ctx.beginPath();
      // Rounded rectangle body from x=-6 to x=6 (nose cone extends further)
      ctx.moveTo(-6, -3);
      ctx.lineTo(6, -3);
      ctx.lineTo(10, 0);        // nose tip
      ctx.lineTo(6, 3);
      ctx.lineTo(-6, 3);
      ctx.closePath();
      ctx.fill();
      // Rim outline
      ctx.strokeStyle = rim;
      ctx.lineWidth = 1.2;
      ctx.stroke();
      ctx.restore();

      // Warhead tip glow
      ctx.save();
      ctx.shadowColor = '#ffffff';
      ctx.shadowBlur = 12;
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(9, 0, 1.5, 0, TAU);
      ctx.fill();
      ctx.restore();

      // Splitter mark — small pink dot on the body
      if (m.splitter) {
        ctx.fillStyle = '#ff3a7d';
        ctx.beginPath();
        ctx.arc(-1, 0, 1.2, 0, TAU);
        ctx.fill();
      }

      ctx.restore();
    }

    // Interceptors + trails
    for (const lc of s.launchers) {
      for (let i = 0; i < lc.trail.length; i++) {
        const t = lc.trail[i];
        const a = i / lc.trail.length;
        ctx.globalAlpha = a * 0.9;
        ctx.fillStyle = lc.color;
        ctx.fillRect(t.x - 1, t.y - 1, 3, 3);
      }
      ctx.globalAlpha = 1;
      ctx.save();
      ctx.shadowColor = lc.color;
      ctx.shadowBlur = 18;
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(lc.x, lc.y, 3.6, 0, TAU);
      ctx.fill();
      ctx.restore();
    }

    // Blasts — glowing rings
    for (const bl of s.blasts) {
      const p = bl.t / bl.dur;
      const alpha = p < 0.35 ? 1 : Math.max(0, 1 - (p - 0.35) * 1.6);
      ctx.save();
      ctx.globalAlpha = alpha * 0.9;
      ctx.shadowColor = bl.color;
      ctx.shadowBlur = 30;
      // Inner filled circle
      const fill = ctx.createRadialGradient(bl.x, bl.y, 0, bl.x, bl.y, bl.r);
      fill.addColorStop(0, `${bl.color}dd`);
      fill.addColorStop(0.6, `${bl.color}55`);
      fill.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = fill;
      ctx.beginPath(); ctx.arc(bl.x, bl.y, bl.r, 0, TAU); ctx.fill();
      // Bright outer ring
      ctx.strokeStyle = bl.color;
      ctx.lineWidth = 2;
      ctx.beginPath(); ctx.arc(bl.x, bl.y, bl.r, 0, TAU); ctx.stroke();
      ctx.restore();
    }

    // Anchors
    for (const a of s.anchors) {
      a.pulse += 0.06;
      const alive = a.alive;
      ctx.save();
      ctx.translate(a.x, a.y);
      if (alive) {
        ctx.shadowColor = a.color;
        ctx.shadowBlur = 16;
      }
      // Base hexagon
      ctx.strokeStyle = alive ? a.color : '#3a3f55';
      ctx.fillStyle = alive ? `${a.color}22` : 'rgba(60,60,80,0.15)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      for (let i = 0; i < 6; i++) {
        const ang = (i / 6) * TAU;
        const rx = Math.cos(ang) * ANCHOR_RADIUS;
        const ry = Math.sin(ang) * ANCHOR_RADIUS * 0.55;
        if (i === 0) ctx.moveTo(rx, ry); else ctx.lineTo(rx, ry);
      }
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      // Inner glow dot
      if (alive) {
        const pulse = 0.7 + 0.3 * Math.sin(a.pulse);
        ctx.shadowBlur = 8;
        ctx.fillStyle = a.color;
        ctx.beginPath();
        ctx.arc(0, -2, 3 * pulse, 0, TAU);
        ctx.fill();
      } else {
        // Small "X" for lost anchor
        ctx.shadowBlur = 0;
        ctx.strokeStyle = '#6b7280';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(-5, -3); ctx.lineTo(5, 3);
        ctx.moveTo(5, -3); ctx.lineTo(-5, 3);
        ctx.stroke();
      }
      ctx.restore();
    }

    // Particles
    for (const p of s.particles) {
      const a = p.life / p.maxLife;
      ctx.globalAlpha = clamp(a, 0, 1);
      ctx.fillStyle = p.color;
      ctx.fillRect(p.x, p.y, p.size, p.size);
    }
    ctx.globalAlpha = 1;
  }

  /* ---- Start / restart ---- */
  const startPlaying = () => {
    const dim = fit();
    if (!dim) return;
    const cfg = mode === 'campaign' ? level : synthesizeEndlessCfg();
    stateRef.current = buildState(dim.w, dim.h, cfg, mode, biome);
    setHudScore(0);
    setHudAnchors(6);
    setHudTime(mode === 'campaign' ? (level.duration || 60) : 0);
    setPaused(false);
    setRunResult(null);
    // Freeze physics during 3-2-1-GO countdown; render still runs.
    pausedRef.current = true;
    setView('playing');
    lastTsRef.current = 0;
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(loop);
    playSfx('button', { volume: 0.3 });
    beginCountdown(() => {
      pausedRef.current = false;
      lastTsRef.current = 0;
    });
  };

  const togglePause = () => {
    if (view !== 'playing') return;
    if (countdown) return;
    const next = !pausedRef.current;
    pausedRef.current = next;
    setPaused(next);
    playSfx('button', { volume: 0.25 });
  };

  const advanceLevel = () => {
    const nx = Math.min(MAX_LEVEL, levelId + 1);
    setLevelId(nx);
    setTimeout(startPlaying, 60);
  };

  function synthesizeEndlessCfg() {
    return {
      id: 0,
      biome: 6,
      name: 'Endless Calm',
      intro: 'No timer. Guard until the last anchor falls.',
      duration: 0,
      missileSpeed: 78,
      spawnEvery: 1.1,
      splitterFrac: 0.2,
      blastRadius: 66,
      launchCooldown: 0.25,
      isBoss: false,
    };
  }

  /* ---- Input ---- */
  const onCanvasClick = (e) => {
    if (view !== 'playing' || paused || countdown) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const cx = (e.clientX - rect.left) * scaleX;
    const cy = (e.clientY - rect.top) * scaleY;
    const s = stateRef.current;
    if (!s) return;
    // Only fire above the anchor line
    if (cy > s.anchorY - 8) return;
    fireInterceptor(s, cx, cy);
  };

  /* ---- Lifecycle ---- */
  useEffect(() => {
    fit();
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      const g = ctx.createLinearGradient(0, 0, 0, canvas.height);
      g.addColorStop(0, biome.bg[0]);
      g.addColorStop(1, biome.bg[1]);
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    const onResize = () => fit();
    window.addEventListener('resize', onResize);
    const onVis = () => { pausedRef.current = document.hidden; };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [fit, biome]);

  /* ---- Renders ---- */
  if (view === 'map') {
    return (
      <section ref={sectionRef} className="bg-white rounded-3xl card-soft border border-navy/5 overflow-hidden scroll-mt-4"
               data-testid="calm-command">
        <div className="relative w-full aspect-[16/9] overflow-hidden"
             style={{ background: `linear-gradient(135deg, #0a0326 0%, #38104a 55%, #1a0d3a 100%)` }}>
          <div className="absolute inset-0 opacity-70"
               style={{
                 backgroundImage: `linear-gradient(rgba(125,250,255,0.14) 1px, transparent 1px), linear-gradient(90deg, rgba(255,92,255,0.10) 1px, transparent 1px)`,
                 backgroundSize: '40px 40px, 40px 40px',
                 maskImage: 'radial-gradient(ellipse at center, black 40%, transparent 80%)',
                 WebkitMaskImage: 'radial-gradient(ellipse at center, black 40%, transparent 80%)',
               }}/>
          <img src="/images/lounge/calm-command.png"
               alt="Calm Command — Hold the ground. Neutralize the noise."
               className="absolute inset-0 w-full h-full object-cover"
               loading="lazy"
               onError={(e) => { e.currentTarget.style.display = 'none'; }}/>
        </div>

        <div className="p-4 lg:p-6 space-y-5">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-navy/40 font-semibold">Campaign progress</p>
              <p className="font-heading text-lg text-navy mt-0.5">
                {Math.max(0, (cc?.highestUnlocked ?? 1) - 1)} of {MAX_LEVEL} cleared ·{' '}
                <span className="text-gold">
                  {Object.values(cc?.levelStars ?? {}).reduce((a, b) => a + b, 0)}
                </span> / {MAX_LEVEL * 3} stars
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setSfxMuted(!muted)} data-testid="cc-mute"
                      className="rounded-full w-9 h-9 flex items-center justify-center border border-navy/10 text-navy/70 hover:text-navy hover:border-navy/25 transition-smooth"
                      aria-label={muted ? 'Unmute' : 'Mute'}>
                {muted ? <VolumeX size={14}/> : <Volume2 size={14}/>}
              </button>
              <button type="button" onClick={() => { setLevelId(cc?.lastLevel || 1); setMode('campaign'); setView('ready'); scrollSectionIntoView(); }}
                      data-testid="cc-resume"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-navy text-white font-semibold text-sm hover:brightness-110 transition-smooth">
                Resume Level {cc?.lastLevel || 1} <ChevronRight size={14}/>
              </button>
            </div>
          </div>

          <CalmCommandLevelSelect
            cc={cc}
            onPick={(id) => { setLevelId(id); setMode('campaign'); setView('ready'); playSfx('button', { volume: 0.3 }); scrollSectionIntoView(); }}
            onEndless={() => { setMode('endless'); setView('ready'); playSfx('unlock', { volume: 0.3 }); scrollSectionIntoView(); }}
          />

          <p className="text-center text-xs text-navy/40 leading-relaxed">
            Clear a level to unlock the next. Tap the sky to launch calm blasts — hold your six anchors until the timer runs out.
          </p>
        </div>
      </section>
    );
  }

  const currentBest = mode === 'endless' ? (cc?.endlessBest ?? 0) : (cc?.levelBest?.[levelId]?.anchorsLeft ?? null);

  return (
    <section ref={sectionRef} className="bg-white rounded-3xl card-soft border border-navy/5 overflow-hidden scroll-mt-4" data-testid="calm-command">
      <div className="px-4 lg:px-5 pt-4 pb-2 flex items-center justify-between gap-3">
        <button type="button" onClick={() => setView('map')} data-testid="cc-back-map"
                className="text-xs text-navy/60 hover:text-navy inline-flex items-center gap-1.5 transition-smooth">
          ← Level select
        </button>
        <div className="text-center">
          <p className="text-[9px] uppercase tracking-[0.28em] text-navy/40 font-semibold">
            {mode === 'campaign' ? `Biome ${biome.id} · ${biome.name}` : 'Endless Calm'}
          </p>
          <p className="font-heading text-base text-navy mt-0.5">
            {mode === 'campaign' ? `Level ${levelId} — ${level.name}` : 'Guard until the last anchor falls'}
          </p>
        </div>
        <button type="button" onClick={() => setView('map')} data-testid="cc-home"
                className="text-xs text-navy/60 hover:text-navy inline-flex items-center gap-1.5 transition-smooth"
                aria-label="Back to level select">
          <Home size={13}/>
        </button>
      </div>

      <div className="px-4 lg:px-5 pb-4">
        <div ref={wrapperRef} className="relative rounded-2xl overflow-hidden border border-navy/10 select-none">
          <canvas
            ref={canvasRef}
            className={`block w-full touch-none cursor-crosshair ${view === 'ready' ? 'hidden' : ''}`}
            data-testid="cc-canvas"
            onClick={onCanvasClick}
          />

          {/* 3-2-1-GO countdown (shared across all Lounge games) */}
          <LoungeCountdown n={countdown} testid="cc-countdown" color="#7dfaff" />

          {/* HUD overlay */}
          <div className="absolute top-3 left-3 right-3 flex items-start justify-between gap-2 pointer-events-none z-10">
            <div className="flex items-center gap-2">
              <div className="rounded-xl px-3 py-1.5 bg-black/45 backdrop-blur-md border border-white/10">
                <p className="text-[9px] uppercase tracking-widest text-white/60 leading-none">Neutralized</p>
                <p className="font-heading text-2xl text-white leading-none mt-0.5 tabular-nums" data-testid="cc-score">{hudScore}</p>
              </div>
              <div className="rounded-xl px-3 py-1.5 bg-black/45 backdrop-blur-md border border-white/10">
                <p className="text-[9px] uppercase tracking-widest text-white/60 leading-none">Anchors</p>
                <p className="font-heading text-2xl leading-none mt-0.5 tabular-nums" data-testid="cc-anchors"
                   style={{ color: hudAnchors >= 4 ? '#7dfaff' : hudAnchors >= 2 ? '#ffcf3a' : '#ff5c8a' }}>
                  {hudAnchors}<span className="text-white/40 text-base">/6</span>
                </p>
              </div>
              {mode === 'campaign' && (
                <div className="rounded-xl px-3 py-1.5 bg-black/45 backdrop-blur-md border border-white/10">
                  <p className="text-[9px] uppercase tracking-widest text-white/60 leading-none">Time</p>
                  <p className="font-heading text-2xl leading-none mt-0.5 tabular-nums" data-testid="cc-time"
                     style={{ color: hudTime <= 5 ? '#ff5c8a' : hudTime <= 10 ? '#ffcf3a' : '#ffffff' }}>
                    {formatMMSS(hudTime)}
                  </p>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              {mode === 'campaign' && (
                <div className="rounded-xl px-3 py-1.5 bg-black/45 backdrop-blur-md border border-gold/25 text-right">
                  <p className="text-[9px] uppercase tracking-widest text-white/60 leading-none">Best</p>
                  <div className="mt-0.5"><StarBadge count={bestStars} size={11} testid="cc-hud-best-stars"/></div>
                </div>
              )}
              {mode !== 'campaign' && (
                <div className="rounded-xl px-3 py-1.5 bg-black/45 backdrop-blur-md border border-gold/25 text-right">
                  <p className="text-[9px] uppercase tracking-widest text-white/60 leading-none">Best</p>
                  <p className="font-heading text-2xl text-gold leading-none mt-0.5 tabular-nums" data-testid="cc-hud-best">{currentBest ?? 0}</p>
                </div>
              )}
            </div>
          </div>

          {/* Pause button */}
          {view === 'playing' && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 pointer-events-auto z-10">
              <button onClick={togglePause} data-testid="cc-pause"
                className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold text-navy bg-gold hover:brightness-95 transition-smooth shadow">
                {paused ? <><Play size={12}/> Resume</> : <><Pause size={12}/> Pause</>}
              </button>
            </div>
          )}

          {/* Paused overlay */}
          {view === 'playing' && paused && (
            <div className="absolute inset-0 backdrop-blur-md flex flex-col items-center justify-center text-white z-20 px-5 text-center"
                 style={{ background: 'linear-gradient(135deg, rgba(11,25,44,0.9) 0%, rgba(58,15,122,0.9) 100%)' }}
                 data-testid="cc-paused">
              <p className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: '#7dfaff' }}>Paused</p>
              <p className="font-heading text-2xl mt-2">Take a breath</p>
              <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
                <button onClick={togglePause} data-testid="cc-pause-resume"
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-cyan-300 text-navy font-semibold hover:brightness-95 text-sm">
                  <Play size={14}/> Resume
                </button>
                <button onClick={() => { cancelCountdown(); setView('map'); }} data-testid="cc-pause-map"
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/8 border border-white/15 text-white hover:bg-white/15 text-sm">
                  Level select
                </button>
              </div>
            </div>
          )}

          {view === 'ready' && (
            <div className="relative w-full backdrop-blur-md flex flex-col items-center text-white z-20 px-5 py-8 text-center"
                 style={{ background: 'linear-gradient(135deg, rgba(11,25,44,0.92) 0%, rgba(58,15,122,0.94) 100%)' }}
                 data-testid="cc-ready">
              <div className="my-auto w-full flex flex-col items-center py-6">
              <p className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: biome.accent }}>
                {mode === 'campaign' ? `Level ${levelId} · ${biome.name}` : 'Endless Calm'}
              </p>
              <p className="font-heading text-3xl lg:text-4xl mt-2 max-w-lg leading-tight">
                {mode === 'campaign' ? level.name : 'Guard until the last anchor falls'}
              </p>
              {mode === 'campaign' && (
                <p className="mt-3 text-sm text-white/80 max-w-md italic">{level.intro}</p>
              )}
              <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-xs text-white/75">
                {mode === 'campaign' && (
                  <>
                    <span className="px-2.5 py-1 rounded-full bg-white/8 border border-white/10 inline-flex items-center gap-1">
                      <Shield size={10} className="text-cyan-300"/> Survive {formatMMSS(level.duration)}
                    </span>
                    <span className="px-2.5 py-1 rounded-full bg-white/8 border border-white/10">
                      Anchors · <span className="text-cyan-300 tabular-nums">6</span>
                    </span>
                    {level.isBoss && (
                      <span className="px-2.5 py-1 rounded-full text-navy font-semibold" style={{ background: biome.glow }}>
                        BOSS WAVE
                      </span>
                    )}
                  </>
                )}
              </div>
              <div className="mt-4 text-xs text-white/60 leading-relaxed max-w-sm">
                <p><span className="text-cyan-300 font-semibold">Tap the sky</span> to launch a calm blast. Anything the blast touches is neutralized.</p>
              </div>
              <button onClick={startPlaying} data-testid="cc-start"
                className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-cyan-300 text-navy font-semibold hover:brightness-95 transition-smooth">
                <Play size={15}/> Start
              </button>
              </div>
            </div>
          )}

          {view === 'over' && runResult && (
            <div className="absolute inset-0 backdrop-blur-md flex flex-col items-center justify-center text-white z-20 px-5 text-center"
                 style={{ background: 'linear-gradient(135deg, rgba(11,25,44,0.94) 0%, rgba(58,15,122,0.95) 100%)' }}
                 data-testid="cc-over">
              <p className={`text-[10px] uppercase tracking-[0.32em] font-semibold ${runResult.cleared ? 'text-cyan-300' : 'text-red-300'}`}>
                {runResult.cleared ? (mode === 'campaign' ? `Level ${levelId} held` : 'Nice hold') : (mode === 'campaign' ? 'Anchors lost' : 'End of run')}
              </p>
              {mode === 'campaign' && runResult.cleared && (
                <div className="mt-3"><StarBadge count={runResult.stars} size={30} testid="cc-over-stars"/></div>
              )}
              <p className="font-heading text-3xl lg:text-4xl mt-3">
                {runResult.score} <span className="text-white/50 text-base">neutralized</span>
              </p>
              <p className="text-xs text-white/60 mt-1">
                {runResult.anchorsLeft} of 6 anchors alive
              </p>
              <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
                <button onClick={startPlaying} data-testid="cc-retry"
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-cyan-300 text-navy font-semibold hover:brightness-95 text-sm">
                  <RotateCcw size={14}/> Play again
                </button>
                {mode === 'campaign' && runResult.cleared && levelId < MAX_LEVEL && (
                  <button onClick={advanceLevel} data-testid="cc-next-level"
                          className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/90 text-navy font-semibold hover:brightness-95 text-sm">
                    <Sparkles size={14}/> Next level
                  </button>
                )}
                <button onClick={() => setView('map')} data-testid="cc-over-map"
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/8 border border-white/15 text-white hover:bg-white/15 text-sm">
                  Level select
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
